/**
 * Pure Algolia query builders for the three operational reservation widgets on the
 * reservations dashboard: Action Items, Upcoming Events and Past Events.
 *
 * These deliberately mirror the filter strings produced by `applyFilters` in
 * store/slices/event_reservations.js. An Action Items tile deep-links into the Events
 * page, so the tile count has to be produced by the same index and the same filter
 * semantics the Events page will use once the user lands there — otherwise the count
 * and the resulting list drift apart.
 *
 * Two things matter for parity:
 *  - the six-month/current `reservations` index (and its sort replicas), NOT `reservations_all`
 *  - the default referral exclusion and the `event_day_end` upcoming/past split
 */

/** Referral exclusion the Events page applies by default (DEFAULT_ALGOLIA_FILTERS). */
export const REFERRAL_EXCLUSION = 'NOT tags:"referral"';

/** Statuses that never belong in the Past Events widget. */
export const PAST_EXCLUDED_STATUSES = ["Cancelled", "Denied"];

/** The Events page collapses the `?type=approved` deep link into these two statuses. */
export const UPCOMING_STATUSES = ["Approved", "Confirmed"];

export const UPCOMING_LIMIT = 5;
export const PAST_LIMIT = 10;

/**
 * Action Items tiles, in render order, each with the status set and timeframe the
 * Events page derives from its `?type=` query param.
 * @see components/widgets/WidgetActionTiles.js for the matching hrefs
 */
export const ACTION_TILES = [
  { key: "pending", statuses: ["Pending Approval"], timeframe: "upcoming" },
  { key: "missingInfo", statuses: ["Missing Info"], timeframe: "upcoming" },
  { key: "upcoming", statuses: UPCOMING_STATUSES, timeframe: "upcoming" },
  { key: "pendingResults", statuses: ["Pending Results"], timeframe: "past" },
  { key: "confirmResults", statuses: ["Confirm Results"], timeframe: "past" },
  { key: "pendingPayment", statuses: ["Pending Payment"], timeframe: "past" },
  { key: "completed", statuses: ["Completed"], timeframe: "past" },
];

/** Tiles needing their own count query — `upcoming` reuses the Upcoming Events query. */
export const COUNT_TILES = ACTION_TILES.filter((t) => t.key !== "upcoming");

const statusClause = (statuses) =>
  `(${statuses.map((s) => `status:"${s}"`).join(" OR ")})`;

const timeframeClause = (timeframe, nowUnix) =>
  timeframe === "past"
    ? `event_day_end < ${nowUnix}`
    : `event_day_end > ${nowUnix}`;

/**
 * Build one Algolia `filters` string using the Events page's clause order:
 * referral exclusion, then timeframe, then status facets.
 * @param {{statuses?: string[], timeframe: 'upcoming'|'past', excludeStatuses?: string[], nowUnix: number}} opts
 * @returns {string}
 */
export const buildReservationFilter = ({
  statuses,
  timeframe,
  excludeStatuses = [],
  nowUnix,
}) => {
  const parts = [REFERRAL_EXCLUSION, timeframeClause(timeframe, nowUnix)];
  if (statuses && statuses.length) parts.push(statusClause(statuses));
  excludeStatuses.forEach((s) => parts.push(`NOT status:"${s}"`));
  return parts.join(" AND ");
};

/**
 * Resolve the six-month reservations index and its two sort replicas for a Firebase
 * project id. Mirrors the suffix logic in store/slices/algolia.js.
 * @param {string} projectId
 */
export const reservationIndexNames = (projectId) => {
  const mode =
    String(projectId || "").split("-").pop() === "staging" ? "-staging" : "";
  const base = `reservations${mode}`;
  return {
    base,
    eventDayAsc: `${base}_event_day_asc`,
    eventDayDesc: `${base}_event_day_desc`,
  };
};

/** Params for a count-only query — no hits, no highlighting, no analytics noise. */
const COUNT_PARAMS = {
  hitsPerPage: 0,
  attributesToRetrieve: [],
  attributesToHighlight: [],
  analytics: false,
};

/**
 * Build the full `multipleQueries` payload backing all three widgets in one request.
 * @param {{projectId: string, nowUnix: number}} opts
 * @returns {{requests: object[], countTileKeys: string[]}}
 */
export const buildReservationWidgetRequests = ({ projectId, nowUnix }) => {
  const idx = reservationIndexNames(projectId);

  const requests = COUNT_TILES.map((tile) => ({
    indexName: idx.base,
    query: "",
    params: {
      ...COUNT_PARAMS,
      filters: buildReservationFilter({
        statuses: tile.statuses,
        timeframe: tile.timeframe,
        nowUnix,
      }),
    },
  }));

  // Serves both the Upcoming Events rows and the `upcoming` tile count (nbHits).
  requests.push({
    indexName: idx.eventDayAsc,
    query: "",
    params: {
      hitsPerPage: UPCOMING_LIMIT,
      analytics: false,
      filters: buildReservationFilter({
        statuses: UPCOMING_STATUSES,
        timeframe: "upcoming",
        nowUnix,
      }),
    },
  });

  requests.push({
    indexName: idx.eventDayDesc,
    query: "",
    params: {
      hitsPerPage: PAST_LIMIT,
      analytics: false,
      filters: buildReservationFilter({
        timeframe: "past",
        excludeStatuses: PAST_EXCLUDED_STATUSES,
        nowUnix,
      }),
    },
  });

  return { requests, countTileKeys: COUNT_TILES.map((t) => t.key) };
};

/**
 * Reduce an Algolia hit to the fields the widget tables render. Brand/external-id
 * prefixing is deliberately left to render time (see eventLocationLabel) so rows do
 * not depend on brand metadata having loaded before the search resolves.
 */
export const toEventRow = (hit) => ({
  id: hit.objectID,
  status: hit.status || "",
  event_day_text: hit.event_day_text || "",
  event_name: hit.event_name || "",
  location_name: hit.location_name || "",
  brand_id: hit.brand_id || null,
  brand_name: hit.brand_name || "",
  external_id:
    hit.external_id === null || hit.external_id === undefined
      ? ""
      : String(hit.external_id),
  fundraiser_org_name: hit.fundraiser_org_name || "",
});

/**
 * Map the ordered `multipleQueries` results back onto the three widgets.
 * @param {object[]} results
 * @param {string[]} countTileKeys
 */
export const parseReservationWidgetResults = (results, countTileKeys) => {
  const list = results || [];
  const counts = {};
  countTileKeys.forEach((key, i) => {
    counts[key] = list[i]?.nbHits ?? 0;
  });

  const upcomingRes = list[countTileKeys.length];
  const pastRes = list[countTileKeys.length + 1];
  counts.upcoming = upcomingRes?.nbHits ?? 0;

  return {
    actionTiles: counts,
    upcomingEvents: (upcomingRes?.hits || []).map(toEventRow),
    pastEvents: (pastRes?.hits || []).map(toEventRow),
  };
};

/**
 * Compose the location cell, matching what buildAggregates used to store: brand name
 * prefix when the account spans multiple brands, external id prefix for brands flagged
 * `includeExternal`.
 */
export const eventLocationLabel = (
  row,
  { multiBrand = false, includeExternalBrandIDs = [] } = {}
) => {
  const parts = [];
  if (multiBrand && row.brand_name) parts.push(row.brand_name);
  if (row.external_id && includeExternalBrandIDs.includes(row.brand_id)) {
    parts.push(row.external_id);
  }
  if (row.location_name) parts.push(row.location_name);
  return parts.length ? parts.join(" - ") : "—";
};
